import { useEffect, useState } from 'react';
import { URL_CONFIG } from '@/configs/environment';
import { useTranslation } from 'react-i18next';
import { deleteJapanText } from '@/utils/utils';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';

interface PositionInterface {
    lat: number,
    lng: number
}

interface GoogleMapProps {
    positionInput: PositionInterface | undefined,
    onChangePosition: (position: { lat: number, lng: number }) => void
}

const positionDefault = { lat: 35.6585805, lng: 139.7454329 };

export default observer(function GoogleMap({ positionInput, onChangePosition }: GoogleMapProps) {
    // hook
    const { t } = useTranslation('translation');

    // store
    const {
        generalStore: { getGgmapGeocodeByLatLng }
    } = useStore();

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(
        null
    );
    const [marker, setMarker] =
        useState<google.maps.marker.AdvancedMarkerElement | null>(null);

    useEffect(() => {
        loadGoogleMapsScript().then(initMap).catch(console.error);
    }, []);

    useEffect(() => {
        if (map && marker && positionInput) {
            marker.position = { ...positionInput };
            map?.setCenter(positionInput);
        }

        // Hiển thị địa chỉ ban đầu
        if (positionInput) {
            setContentInfoWindow(positionInput);
        }
    }, [map, marker, positionInput]);

    const loadGoogleMapsScript = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (window.google?.maps) {
                resolve();
                return;
            }

            const scriptId = 'google-maps-script';
            if (document.getElementById(scriptId)) {
                document
                    .getElementById(scriptId)
                    ?.addEventListener('load', () => resolve());
                return;
            }

            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${URL_CONFIG.GOOGLE_MAP.private}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () =>
                reject(new Error('Google Maps script failed to load.'));
            document.body.appendChild(script);
        });
    };

    const initMap = async () => {
        if (!window.google?.maps) {
            console.error('Google Maps API not ready yet!');
            return;
        }

        const { Map, InfoWindow } = (await google.maps.importLibrary(
            'maps'
        )) as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
            'marker'
        )) as google.maps.MarkerLibrary;

        const mapInstance = new Map(document.getElementById('map') as HTMLElement, {
            center: positionInput || positionDefault,
            zoom: 12,
            mapId: URL_CONFIG.GOOGLE_MAP.mapId,
            mapTypeControl: false
        });

        setMap(mapInstance);

        const infoWindowTmp = new InfoWindow();
        setInfoWindow(infoWindowTmp);

        const markerInstance = new AdvancedMarkerElement({
            position: positionInput,
            map: mapInstance,
            gmpDraggable: true,
            title: 'This marker is draggable.'
        });

        markerInstance.addListener('dragend', async () => {
            const position = markerInstance.position as google.maps.LatLng;
            if (position) {
                const newPos = { lat: Number(position.lat), lng: Number(position.lng) };

                infoWindowTmp.close();
                const addressText = await getAddressTextFromLatLng(
                    Number(newPos.lat),
                    Number(newPos.lng)
                );
                infoWindowTmp.setContent(addressText);
                infoWindowTmp.open(mapInstance, markerInstance);

                onChangePosition(newPos);
            }
        });

        setMarker(markerInstance);
    };

    const getAddressTextFromLatLng = async (lat: number, lng: number) => {
        try {
            const res = await flowResult(getGgmapGeocodeByLatLng({
                latitude: lat,
                longitude: lng
            }));

            if (res) {
                const address = deleteJapanText(res.address);
                return address;
            } else {
                return `${t('words_title.no_address')}`;
            }
        } catch (error) {
            console.error('Error getting address:', error);
            return `${t('words_title.no_address')}`;
        }
    };

    const setContentInfoWindow = async (positionInput: PositionInterface) => {
        const address = await getAddressTextFromLatLng(
            positionInput?.lat,
            positionInput?.lng
        );
        infoWindow?.setContent(address);
        infoWindow?.open(map, marker);
    };

    return <div id='map' className='w-full h-full' />;
});
